import { useScopedAppFlagStore } from '@frontend/scope';

/**
 * Feature flag controlling access to messaging routes and features.
 *
 * Configured to default ON at the flag service: when the flag is enabled
 * (the default), all messaging routes and features are accessible. When
 * explicitly turned OFF, users are blocked from accessing them.
 *
 * NOTE: This will eventually be migrated to a customization flag check.
 */
export const MESSAGING_FEATURES_FLAG = 'nwx:messaging-features-flag';

/**
 * Returns whether the user has access to messaging routes and features.
 *
 * Default-on contract: messaging is enabled unless the flag has been
 * explicitly set to `false` for every selected location. In particular:
 *
 *   - `undefined` / unregistered flag       → enabled (true)
 *   - any selected location with `true`     → enabled (true)
 *   - mix of `false` and `undefined`        → enabled (true) — only one
 *                                              location was explicitly opted
 *                                              out; the unknown ones default on
 *   - every selected location with `false`  → disabled (false)
 *   - no selected locations                 → enabled (true)
 *
 * Implementation note: we cannot rely on `getFeatureFlagValue` alone because
 * its `ANY` strategy collapses unknown values to `false`, which would break
 * the default-on contract in environments where the flag has not been
 * registered (e2e tests, component tests, fresh sessions before hydration).
 */
export const useMessagingFeaturesEnabled = (): boolean => {
  const { getFeatureFlag } = useScopedAppFlagStore();
  // Defensive: in some test environments `useScopedAppFlagStore` is mocked
  // with a partial implementation that omits `getFeatureFlag`. Treat that as
  // "no signal" and honor the default-on contract.
  if (typeof getFeatureFlag !== 'function') {
    return true;
  }
  const records = getFeatureFlag(MESSAGING_FEATURES_FLAG) ?? [];
  // Messaging is enabled unless every selected location explicitly opted out.
  return records.length === 0 || !records.every((record) => record?.value === false);
};
